import { ANALYTICS_SCHEMA_VERSION, WEB_PLATFORM } from './posthogConstants';
import { analyticsEnvironment } from './env';
import type {
	AnalyticsSpaceSnapshot,
	AnalyticsTenantSnapshot,
	AnalyticsUserContext,
} from './types';

const emptyTenant: AnalyticsTenantSnapshot = {
	communityId: '',
	communityName: '',
	domain: '',
};

const emptySpace: AnalyticsSpaceSnapshot = {
	groupId: '',
	groupName: '',
};

let tenantSnapshot: AnalyticsTenantSnapshot = {...emptyTenant};
let spaceSnapshot: AnalyticsSpaceSnapshot = {...emptySpace};
let userSnapshot: Partial<AnalyticsUserContext> = {};

export function getTenantSnapshot(): AnalyticsTenantSnapshot {
	return tenantSnapshot;
}

export function setTenantSnapshot(partial: Partial<AnalyticsTenantSnapshot>): void {
	tenantSnapshot = {
		...tenantSnapshot,
		...partial,
	};
}

export function resetTenantSnapshot(domain = ''): void {
	tenantSnapshot = {...emptyTenant, domain};
}

export function getSpaceSnapshot(): AnalyticsSpaceSnapshot {
	return spaceSnapshot;
}

export function setSpaceSnapshot(partial: Partial<AnalyticsSpaceSnapshot>): void {
	spaceSnapshot = {
		...spaceSnapshot,
		...partial,
	};
}

export function resetSpaceSnapshot(): void {
	spaceSnapshot = {...emptySpace};
}

export function getUserSnapshot(): Partial<AnalyticsUserContext> {
	return userSnapshot;
}

export function setUserSnapshot(user: Partial<AnalyticsUserContext>): void {
	userSnapshot = user;
}

export function resetUserSnapshot(): void {
	userSnapshot = {};
}

/**
 * Context merged onto every event (manual track + autocapture via enrichment plugin).
 * Explicit event properties win; empty values are filled from snapshots.
 */
export function mergeEventContextProperties(
	props: Record<string, unknown>
): Record<string, unknown> {
	const communityId =
		(props.communityId as string) || tenantSnapshot.communityId || '';
	const communityName =
		(props.communityName as string) || tenantSnapshot.communityName || '';
	const domain = (props.domain as string) || tenantSnapshot.domain || '';
	const groupId = (props.groupId as string) || spaceSnapshot.groupId || '';
	const groupName = (props.groupName as string) || spaceSnapshot.groupName || '';
	const userRole =
		(props.userRole as string) || userSnapshot.userRole || '';
	const roleId = (props.roleId as string) || userSnapshot.roleId || '';
	// One spelling of the ids (event catalog): community_id / space_id on every event. The
	// camelCase keys stay for one release so existing tiles keep working, then go.
	const communityIdStd = (props.community_id as string) || communityId;
	const spaceIdStd = (props.space_id as string) || groupId;
	const userId = (props.user_id as string) || userSnapshot.userId || '';

	return {
		...props,
		communityId,
		communityName,
		domain,
		groupId,
		groupName,
		...(userRole ? {userRole} : {}),
		...(roleId ? {roleId} : {}),
		...(communityIdStd ? {community_id: communityIdStd} : {}),
		...(spaceIdStd ? {space_id: spaceIdStd} : {}),
		...(userId ? {user_id: userId} : {}),
		platform: WEB_PLATFORM,
		environment: analyticsEnvironment(),
		schema_version: ANALYTICS_SCHEMA_VERSION,
	};
}
